import { Component, OnDestroy, OnInit } from '@angular/core';
import { EmailAuthProvider, User, UserInfo } from '@angular/fire/auth';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { SocialSigninService } from '../../social-signin/social-signin.service';
import { ToastService } from '../../toast/toast';

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
}

@Component({
    standalone: false,
    selector: 'abs-linked-accounts',
    template: `
        <div>
            <h3>{{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.TITLE' | translate }}</h3>

            <ion-item *ngFor="let provider of linkedProviders">
                <ion-label>{{ 'GLOBAL.LOGIN.PROVIDERS.' + provider.providerId | translate }}</ion-label>
                <ion-button slot="end" [disabled]="!canUnlink(provider.providerId)" (click)="unlink(provider.providerId)">
                    {{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.UNLINK' | translate }}
                </ion-button>
            </ion-item>
            <abs-reauth-prompt *ngIf="pendingUnlinkProviderId" (reauthenticated)="onReauthenticated()"></abs-reauth-prompt>

            <form
                *ngIf="!hasPasswordProvider"
                [formGroup]="passwordForm"
                (ngSubmit)="addPassword()"
                class="gutter-collapse vertical-spacing"
            >
                <p>{{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.DESCRIPTION' | translate: { email: userEmail } }}</p>
                <div class="form-row">
                    <ion-label>{{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.PASSWORD' | translate }}</ion-label>
                    <ion-input type="password" formControlName="password"></ion-input>
                </div>
                <div class="form-row">
                    <ion-label>{{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.CONFIRM_PASSWORD' | translate }}</ion-label>
                    <ion-input type="password" formControlName="confirmPassword"></ion-input>
                </div>
                <p class="error" *ngIf="error">{{ 'GLOBAL.ERRORS.' + error | translate }}</p>
                <div class="buttons">
                    <ion-button type="submit" [disabled]="passwordForm.invalid || submitting">
                        {{ 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.SUBMIT' | translate }}
                    </ion-button>
                </div>
            </form>
        </div>
    `
})
export class LinkedAccountsComponent implements OnInit, OnDestroy {
    linkedProviders: UserInfo[] = [];
    pendingUnlinkProviderId: string = null;
    private currentUser: User | null = null;
    private authSubscription: Subscription;

    submitting = false;
    error: string = null;

    passwordForm = this.fb.group(
        {
            password: this.fb.control('', [Validators.required, Validators.minLength(8)]),
            confirmPassword: this.fb.control('', [Validators.required])
        },
        { validators: passwordsMatchValidator }
    );

    constructor(
        private socialSignin: SocialSigninService,
        private fb: UntypedFormBuilder,
        private toast: ToastService,
        private translateService: TranslateService
    ) {}

    /**
     * Driven by auth state rather than a one-off read of auth.currentUser.
     * Firebase restores the persisted user asynchronously, so on a page load
     * currentUser is still null while this component initialises: a snapshot
     * captured an empty provider list and never corrected itself, which showed
     * the add-password form to accounts that already had a password, and left
     * the description's {{email}} unresolved.
     */
    ngOnInit() {
        this.authSubscription = this.socialSignin.authState$.subscribe((user) => {
            this.currentUser = user ?? null;
            this.linkedProviders = user?.providerData ?? [];
        });
    }

    ngOnDestroy() {
        this.authSubscription?.unsubscribe();
    }

    get hasPasswordProvider(): boolean {
        return this.linkedProviders.some((p) => p.providerId === 'password');
    }

    /**
     * Firebase leaves the top-level user.email null for some social sign-ins
     * (a provider that grants no email scope, or Apple's private relay), even
     * though the address is present on the provider entry. Falling back to
     * providerData matters twice over: the description below interpolates it,
     * and addPassword() builds the EmailAuthProvider credential from it, so an
     * undefined value silently produced an unusable credential.
     *
     * Ends on an empty string rather than undefined because ngx-translate
     * leaves a parameter it cannot resolve as the literal placeholder, which is
     * how "{{email}}" reached the screen.
     */
    get userEmail(): string {
        return this.currentUser?.email ?? this.linkedProviders.find((p) => p.email)?.email ?? '';
    }

    canUnlink(providerId: string): boolean {
        return this.linkedProviders.length > 1;
    }

    async unlink(providerId: string) {
        if (!this.canUnlink(providerId)) {
            return;
        }

        try {
            const updatedUser = await this.socialSignin.unlinkProvider(this.currentUser, providerId);
            this.linkedProviders = updatedUser.providerData || [];
        } catch (err: any) {
            if (err.code === 'auth/requires-recent-login') {
                this.pendingUnlinkProviderId = providerId;
            }
        }
    }

    async onReauthenticated() {
        const providerId = this.pendingUnlinkProviderId;
        this.pendingUnlinkProviderId = null;
        await this.unlink(providerId);
    }

    async addPassword() {
        if (this.passwordForm.invalid) {
            return;
        }

        this.error = null;
        this.submitting = true;

        try {
            const credential = EmailAuthProvider.credential(this.userEmail, this.passwordForm.value.password);
            const result = await this.socialSignin.linkCredential(this.currentUser, credential);
            this.linkedProviders = result.user.providerData || [];
            this.passwordForm.reset();

            /*
             * Verifying the address is what makes this password credential
             * trusted. While it stays unverified Firebase treats it as the
             * untrusted provider in its verified-email rules, so a later sign-in
             * with a trusted provider (Google) overwrites it and the password
             * silently disappears.
             *
             * A failed send must not surface as a failed link: the password is
             * already set by this point, so an error would be untrue and would
             * invite a retry that fails with auth/provider-already-linked.
             */
            let verificationSent = true;
            try {
                await this.socialSignin.sendVerificationEmail(result.user);
            } catch {
                verificationSent = false;
            }

            const messageKey = verificationSent
                ? 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.SUCCESS'
                : 'GLOBAL.SETTINGS.ACCOUNT.LINKED_ACCOUNTS.ADD_PASSWORD.SUCCESS_UNVERIFIED';
            this.toast.showToast('', this.translateService.instant(messageKey), {
                color: 'success',
                duration: 6000
            });
        } catch (err: any) {
            this.error = err.code || 'UNKNOWN';
        } finally {
            this.submitting = false;
        }
    }
}
